import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, apiError } from "@/lib/api";
import { AuthShell } from "@/pages/Login";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title="Reset your password"
      subtitle="We will email a reset link if an account exists for that address."
      footer={
        <Link to="/login" className="font-medium text-navy-800 underline underline-offset-4">
          Back to log in
        </Link>
      }
    >
      {sent ? (
        <p className="border border-slate-200 bg-slate-100/70 p-4 text-sm text-slate-700" data-testid="forgot-success">
          If an account exists for {email}, a reset link is on its way. The link expires in one hour.
        </p>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <div>
            <Label htmlFor="email" className="text-xs">Work email</Label>
            <Input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="forgot-email-input"
            />
          </div>
          {error && <p className="text-sm text-rose-700" data-testid="forgot-error">{error}</p>}
          <Button
            type="submit"
            disabled={busy}
            className="w-full bg-navy-800 text-white hover:bg-navy-700"
            data-testid="forgot-submit-button"
          >
            {busy ? "Sending…" : "Send reset link"}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const token = params.get("token") || "";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, password });
      navigate("/login");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Choose a new password" subtitle="Set a new password for your account.">
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <div>
          <Label htmlFor="password" className="text-xs">New password (minimum 8 characters)</Label>
          <Input
            id="password"
            type="password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="reset-password-input"
          />
        </div>
        {error && <p className="text-sm text-rose-700" data-testid="reset-error">{error}</p>}
        <Button
          type="submit"
          disabled={busy || !token}
          className="w-full bg-navy-800 text-white hover:bg-navy-700"
          data-testid="reset-submit-button"
        >
          {busy ? "Updating…" : "Update password"}
        </Button>
        {!token && <p className="text-sm text-rose-700">This reset link is missing its token.</p>}
      </form>
    </AuthShell>
  );
}
